<template>
  <div :id="String(el.id)" ref="articlesList" :key="el.id" :data-index="index" class="articles-list__el">
    <div class="articles-list-img-container">
      <AppLink :to="goToArticle(el.slug)">
        <img class="articles-list-img" :alt="el.title" :src="el?.mainPhoto?.thumbnailL" loading="lazy" />
      </AppLink>
    </div>

    <div class="articles-list-content">
      <AppLink :to="goToArticle(el.slug)">
        <span class="articles-list-title" v-html="el.title" />&nbsp;
        <span class="articles-list-subtitle" v-html="el.tagline" />

        <div class="articles-list-time">
          <div class="articles-list-time__reading-time">
            <span class="articles-list-time__reading-time-icon">
              <IconBase name="icon-clock" color="--alias-color-icon-subtle" />
            </span>
            <span class="articles-list-time__reading-time-text">{{ readingTimeText }}</span>
          </div>
        </div>
      </AppLink>
      <div v-if="el.geo" class="articles-list-hashtags">
        <AppLink
          v-for="hashtag in hashtags(el.geo)"
          :key="hashtag.slug"
          class="articles-list__hashtags-el"
          :to="goTo(hashtag.slug)"
          >#{{ hashtag.name }}
        </AppLink>
      </div>
    </div>
  </div>
</template>

<script lang="ts" setup>
import { useI18n } from '@experience/i18n';
import { computed } from 'vue';

import { articleUrl, articleCountryUrl } from '@/shared/helpers/urls';
import ru from '@/shared/i18n/ru';
import IconBase from '@/shared/icons/IconBase.vue';
import type { Article, ArticleGeo } from '@/shared/models/journal.ts';

type MessageSchema = typeof ru;
const { t } = useI18n<{ message: MessageSchema }, 'ru'>({
  inheritLocale: true,
  messages: { ru },
});

const props = defineProps<{
  el: Article;
  index: number;
}>();

const readingTimeText = computed(() => {
  if (!props.el.readingTime) {
    return '';
  }

  return t('readingTime', { n: Math.round(props.el.readingTime / 60) });
});

const goTo = (slug: string): string => articleCountryUrl(slug);

const goToArticle = (slug: string): string => articleUrl(slug);

// В карточке статьи показываем не больше 3 стран-хэштегов, чтобы не ломать вёрстку.
const hashtags = (geo: ArticleGeo) => geo.country?.slice(0, 3) || [];
</script>

<style lang="scss" scoped>
a {
  color: var(--alias-color-link-default);
  text-decoration: none;

  &:hover {
    color: var(--alias-color-link-default-hovered);
  }
}

.articles-list__el {
  cursor: pointer;

  @include adaptivity.tablet {
    width: 100%;
  }

  @include adaptivity.mobile {
    display: flex;
    flex-direction: column;
    width: 100%;
  }
}

.articles-list-img-container:hover img {
  transform: scale(1.1);
}

.articles-list-img-container {
  width: 100%;
  aspect-ratio: 4/3;
  overflow: hidden;
  border-radius: var(--alias-radius-16);
}

.articles-list-img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  transition: transform 0.7s ease;
}

.articles-list-content {
  margin-top: var(--alias-space-16, 16px);

  @include adaptivity.mobile {
    margin-top: var(--alias-space-12, 12px);
  }
}

.articles-list-hashtags {
  display: flex;
  flex-wrap: wrap;
  margin-top: var(--alias-space-8, 8px);
}

.articles-list-hashtags-el {
  margin-right: var(--alias-space-8, 8px);
  text-decoration: none;

  @include typography.base-300-300-regular;
}

.articles-list-dot {
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0 var(--alias-space-4, 4px);
  font-size: 14px;
  color: var(--alias-color-text-subtle);
}

.articles-list-title {
  margin-top: var(--alias-space-16, 16px);
  color: var(--alias-color-text-default);

  @include typography.base-500-400-semi;

  @include adaptivity.mobile {
    @include typography.base-400-400-semi;
  }
}

.articles-list-time {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  margin-top: var(--alias-space-8, 8px);
  font-size: 14px;
  color: var(--alias-color-text-subtle);
}

.articles-list-time__reading-time {
  display: flex;
  gap: 0 var(--alias-space-4, 4px);
}

.articles-list-time__reading-time-icon {
  display: flex;
  align-items: center;
  justify-content: center;
}

.articles-list-time__reading-time-icon .icon {
  fill: var(--alias-color-icon-subtle);
}

.articles-list-time__reading-time-text {
  display: flex;
  align-items: center;
}

.articles__hashtags-el,
.articles-list__hashtags-el {
  margin-right: var(--alias-space-8, 8px);
  font-size: 14px;
  text-decoration: none;
}

.articles-list-subtitle {
  color: var(--alias-color-text-default);
  hyphens: auto;
  overflow-wrap: break-word;

  @include typography.base-500-400-regular;

  @include adaptivity.mobile {
    @include typography.base-400-400-regular;
  }
}
</style>
